import { useState, type FormEvent, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Flex,
  Heading,
  Text,
  TextField,
  Box,
  IconButton,
} from "@radix-ui/themes";
import {
  LockClosedIcon,
  EyeOpenIcon,
  EyeClosedIcon,
  CheckIcon,
  Cross2Icon,
} from "@radix-ui/react-icons";
import { supabase } from "../../lib/supabase";
import { logPasswordEvent } from "../../lib/logPasswordEvent";
import { LanguageSelector } from "./LanguageSelector";
import styles from "./Login.module.css";

export function UpdatePassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasValidSession, setHasValidSession] = useState<boolean | null>(null);

  const passwordRequirements = [
    {
      label: t("updatePassword.requirements.minLength"),
      test: (password: string) => password.length >= 10,
    },
    {
      label: t("updatePassword.requirements.number"),
      test: (password: string) => /\d/.test(password),
    },
    {
      label: t("updatePassword.requirements.uppercase"),
      test: (password: string) => /[A-Z]/.test(password),
    },
    {
      label: t("updatePassword.requirements.special"),
      test: (password: string) =>
        /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    },
  ];

  useEffect(() => {
    // Event 3: Log that the recovery link was clicked (unauthenticated, token_hash correlation)
    // "th" param is appended to redirectTo by the edge function so it survives PKCE redirect
    const params = new URLSearchParams(window.location.search);
    const tokenHash = params.get("th");
    if (tokenHash) {
      logPasswordEvent("recovery_link_clicked", { token_hash: tokenHash });
    }

    // Check if user has a valid recovery session
    let sessionValidatedLogged = false;

    const checkSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session) {
        setHasValidSession(true);
        if (!sessionValidatedLogged) {
          sessionValidatedLogged = true;
          logPasswordEvent(
            "recovery_session_validated",
            {},
            { authenticated: true },
          );
        }
      } else {
        setHasValidSession(false);
        // Log that the session could not be validated
        logPasswordEvent("recovery_session_not_validated", {
          token_hash: tokenHash ?? "",
        });
      }
    };

    checkSession();

    // Listen for auth state changes (when token is extracted from URL)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" && session) {
        setHasValidSession(true);
        if (!sessionValidatedLogged) {
          sessionValidatedLogged = true;
          logPasswordEvent(
            "recovery_session_validated",
            {},
            { authenticated: true },
          );
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const allRequirementsMet = passwordRequirements.every((req) =>
    req.test(password),
  );
  const passwordsMatch = password === confirmPassword && confirmPassword !== "";
  const canSubmit = allRequirementsMet && passwordsMatch && !loading;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    if (!allRequirementsMet) {
      setError(t("updatePassword.errors.requirements"));
      return;
    }

    if (!passwordsMatch) {
      setError(t("updatePassword.errors.passwordsDontMatch"));
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        setError(error.message);
      } else {
        // Event 5: Log password updated
        logPasswordEvent("password_updated", {}, { authenticated: true });
        setSuccess(true);
        // Redirect to login after a short delay
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (hasValidSession === null) {
    return (
      <div className={styles.container}>
        <Box className={styles.form}>
          <Flex direction="column" gap="4" align="center">
            <Text>{t("updatePassword.verifying")}</Text>
            <LanguageSelector />
          </Flex>
        </Box>
      </div>
    );
  }

  if (hasValidSession === false) {
    return (
      <div className={styles.container}>
        <Box className={styles.form}>
          <Flex direction="column" gap="4" align="center">
            <Heading size="6">{t("updatePassword.invalidLink")}</Heading>
            <Text color="gray" size="2" align="center">
              {t("updatePassword.invalidLinkDescription")}
            </Text>
            <Button onClick={() => navigate("/login")} size="3" mt="2">
              {t("updatePassword.backToLogin")}
            </Button>
            <LanguageSelector />
          </Flex>
        </Box>
      </div>
    );
  }

  if (success) {
    return (
      <div className={styles.container}>
        <Box className={styles.form}>
          <Flex direction="column" gap="4" align="center">
            <Box
              style={{
                backgroundColor: "var(--green-3)",
                borderRadius: "50%",
                padding: "1rem",
              }}
            >
              <CheckIcon width={32} height={32} color="var(--green-11)" />
            </Box>
            <Heading size="6">{t("updatePassword.success")}</Heading>
            <Text color="gray" size="2" align="center">
              {t("updatePassword.successDescription")}
            </Text>
            <LanguageSelector />
          </Flex>
        </Box>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <Flex direction="column" gap="4">
          <Box mb="2">
            <Heading size="6" mb="1">
              {t("updatePassword.title")}
            </Heading>
            <Text color="gray" size="2">
              {t("updatePassword.description")}
            </Text>
          </Box>

          <Box>
            <Text as="label" size="2" weight="medium" mb="1">
              {t("updatePassword.newPassword")}
            </Text>
            <TextField.Root
              type={showPassword ? "text" : "password"}
              placeholder={t("updatePassword.newPasswordPlaceholder")}
              size="3"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            >
              <TextField.Slot>
                <LockClosedIcon />
              </TextField.Slot>
              <TextField.Slot>
                <IconButton
                  type="button"
                  size="1"
                  variant="ghost"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeClosedIcon /> : <EyeOpenIcon />}
                </IconButton>
              </TextField.Slot>
            </TextField.Root>
          </Box>

          {/* Password Requirements */}
          <Box
            style={{
              backgroundColor: "var(--gray-2)",
              borderRadius: "6px",
              padding: "0.75rem",
            }}
          >
            <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
              {t("updatePassword.requirementsTitle")}
            </Text>
            <Flex direction="column" gap="1">
              {passwordRequirements.map((req, index) => {
                const isMet = req.test(password);
                return (
                  <Flex key={index} align="center" gap="2">
                    {isMet ? (
                      <CheckIcon color="var(--green-11)" />
                    ) : (
                      <Cross2Icon color="var(--gray-8)" />
                    )}
                    <Text
                      size="1"
                      color={isMet ? "green" : "gray"}
                      style={{
                        textDecoration: isMet ? "line-through" : "none",
                      }}
                    >
                      {req.label}
                    </Text>
                  </Flex>
                );
              })}
            </Flex>
          </Box>

          <Box>
            <Text as="label" size="2" weight="medium" mb="1">
              {t("updatePassword.confirmPassword")}
            </Text>
            <TextField.Root
              type={showConfirmPassword ? "text" : "password"}
              placeholder={t("updatePassword.confirmPasswordPlaceholder")}
              size="3"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            >
              <TextField.Slot>
                <LockClosedIcon />
              </TextField.Slot>
              <TextField.Slot>
                <IconButton
                  type="button"
                  size="1"
                  variant="ghost"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeClosedIcon /> : <EyeOpenIcon />}
                </IconButton>
              </TextField.Slot>
            </TextField.Root>
            {confirmPassword && !passwordsMatch && (
              <Text color="red" size="1" mt="1">
                {t("updatePassword.errors.passwordsDontMatch")}
              </Text>
            )}
            {passwordsMatch && (
              <Flex align="center" gap="1" mt="1">
                <CheckIcon color="var(--green-11)" />
                <Text color="green" size="1">
                  {t("updatePassword.passwordsMatch")}
                </Text>
              </Flex>
            )}
          </Box>

          {error && (
            <Text color="red" size="2">
              {error}
            </Text>
          )}

          <Button type="submit" size="3" mt="2" disabled={!canSubmit}>
            {loading
              ? t("updatePassword.updating")
              : t("updatePassword.submit")}
          </Button>

          <Flex justify="center" mt="2">
            <LanguageSelector />
          </Flex>
        </Flex>
      </form>
    </div>
  );
}
